import { DateTime } from 'luxon';
import { DayClass, FeeType, PricingTier, Site } from '../../generated/graphql';
import { isWeekend } from './date';
import { ZONE } from '../constants';
import { getSiteSpecialTariffs } from './specialTariff';

/**
 * Calculates the cost using pricing tiers
 * @param selectedSite - The site with pricing tiers
 * @param durationMinutes - Duration in minutes
 * @param isWeekendNow - Whether it's currently a weekend
 * @returns The calculated amount based on cumulative tier pricing
 */
const calculateAmountWithTiers = (
  selectedSite: Site,
  durationMinutes: number,
  relevantTiers: PricingTier[]
): number => {
  if (!selectedSite.pricingTiers || selectedSite.pricingTiers.length === 0) {
    return 0;
  }

  if (relevantTiers.length === 0) {
    return 0;
  }
  // Apply free time before calculation
  const freeTime = selectedSite.hasFreeTime ? selectedSite.freeTime : 0;
  const actualDurationMinutes = Math.max(0, durationMinutes - freeTime);
  const durationHours = actualDurationMinutes / 60;

  let totalAmount = 0;
  let hoursProcessed = 0;

  for (const tier of relevantTiers) {
    if (hoursProcessed >= durationHours) {
      break;
    }

    const tierStart = tier.startHours;
    const tierEnd = tier.endHours ?? Infinity; // null endHours means unlimited

    // Calculate the effective start and end for this tier based on remaining hours
    const effectiveStart = Math.max(tierStart, hoursProcessed);
    const effectiveEnd =
      tier.endHours === null ? durationHours : Math.min(tierEnd, durationHours);

    if (effectiveStart < effectiveEnd) {
      const hoursInThisTier = effectiveEnd - effectiveStart;
      totalAmount += hoursInThisTier * tier.costPerHour;
      hoursProcessed = effectiveEnd;
    }
  }

  return Math.round(totalAmount * 100) / 100;
};

const getRelevantTiers = (selectedSite: Site, isWeekendNow: boolean) => {
  const dayClass = isWeekendNow ? DayClass.Weekend : DayClass.Weekday;
  return (
    selectedSite.pricingTiers?.filter(
      (tier): tier is PricingTier => tier !== null && tier.dayClass === dayClass
    ) ?? []
  );
};

const calculateAmountPostPayment = (
  selectedSite: Site | null,
  currentDuration: number,
  startTime: DateTime,
  selectedFee: FeeType,
  specialTariffSelected: number | null
) => {
  let amount = 0;
  if (selectedFee === FeeType.SpecialTariff) {
    const tariffs = getSiteSpecialTariffs(selectedSite);
    const pricing = tariffs.find((x) => x.id === specialTariffSelected);
    if (pricing) {
      amount = pricing.price;
      return amount;
    }
  }

  const currentTimeInAuckland = DateTime.now().setZone(ZONE);
  const startTimeFormat = startTime.toFormat('HH:mm');
  const endTimeFormat = currentTimeInAuckland.toFormat('HH:mm');
  if (selectedSite) {
    const isWeekendNow = isWeekend(currentTimeInAuckland);

    // Get costPerHour for potential use in special tariff logic
    const costPerHour = isWeekendNow
      ? selectedSite.costPerHourWeekend
      : selectedSite.costPerHour;

    const relevantTiers = getRelevantTiers(selectedSite, isWeekendNow);
    // Check if pricing tiers exist and use them
    if (selectedSite.pricingTiers && relevantTiers.length > 0) {
      amount = calculateAmountWithTiers(
        selectedSite,
        currentDuration,
        relevantTiers
      );
    } else {
      // Fall back to regular costPerHour logic
      const freeTime = selectedSite.hasFreeTime ? selectedSite.freeTime : 0;
      const actualDuration = Math.max(0, currentDuration - freeTime);
      amount =
        Math.round((actualDuration / 60) * (costPerHour ?? 0) * 100) / 100;
    }

    const tariffs = getSiteSpecialTariffs(selectedSite);

    if (tariffs.length > 0 && !isWeekendNow) {
      const matchingTariffs = tariffs.filter((x) => startTimeFormat <= x.start);

      if (matchingTariffs.length > 0) {
        const tariffsBeforeEnd = matchingTariffs.filter(
          (x) => x.end >= endTimeFormat
        );

        if (tariffsBeforeEnd.length > 0) {
          const tariff = tariffsBeforeEnd[0];
          amount = tariff.price < amount ? tariff.price : amount;
        }
        const tariffsAfterEnd = matchingTariffs.filter(
          (x) => x.end < endTimeFormat
        );

        if (tariffsAfterEnd.length > 0) {
          const tariff = tariffsAfterEnd[0];
          const endTime = DateTime.fromFormat(tariff.end, 'HH:mm', {
            zone: ZONE,
          });
          const durationInHours = endTime.diff(
            currentTimeInAuckland,
            'hours'
          ).hours;
          amount = tariff.price + durationInHours * (costPerHour ?? 0);
        }
      }
    }
  }
  return amount;
};

export const calculateAmount = (
  selectedSite: Site | null,
  selectedFee: FeeType,
  currentDuration: number,
  reachedMaxDuration: boolean,
  specialTariffSelected: number | null,
  startTime?: DateTime
) => {
  let amount = 0;
  if (selectedSite?.paymentType === 'Postpayment' && startTime) {
    amount = calculateAmountPostPayment(
      selectedSite,
      currentDuration,
      startTime,
      selectedFee,
      specialTariffSelected
    );
  } else {
    if (selectedSite) {
      if (selectedFee === FeeType.Monthly) {
        amount = selectedSite.monthlyFee;
      } else if (selectedFee === FeeType.Weekly) {
        amount = selectedSite.weeklyFee;
      } else if (selectedFee === FeeType.Hourly) {
        if (reachedMaxDuration) {
          const currentTimeInAuckland = DateTime.now();
          amount = isWeekend(currentTimeInAuckland)
            ? selectedSite.maxCostWeekend
            : selectedSite.maxCost;
        } else {
          const currentTimeInAuckland = DateTime.now();
          const isWeekendNow = isWeekend(currentTimeInAuckland);
          const relevantTiers = getRelevantTiers(selectedSite, isWeekendNow);
          // Check if pricing tiers exist and use them
          if (selectedSite.pricingTiers && relevantTiers.length > 0) {
            amount = calculateAmountWithTiers(
              selectedSite,
              currentDuration,
              relevantTiers
            );
          } else {
            // Fall back to regular costPerHour logic
            const costPerHour = isWeekendNow
              ? selectedSite.costPerHourWeekend
              : selectedSite.costPerHour;
            const actualDuration = Math.max(
              0,
              currentDuration -
                (selectedSite.hasFreeTime ? selectedSite.freeTime : 0),
            );
            amount =
              Math.round((actualDuration / 60) * (costPerHour ?? 0) * 100) /
              100;
          }
        }
      } else if (selectedFee === FeeType.SpecialTariff) {
        const tariffs = getSiteSpecialTariffs(selectedSite);
        const pricing = tariffs.find((x) => x.id === specialTariffSelected);
        if (pricing) {
          amount = pricing.price;
        }
      }
    }
  }
  return selectedSite ? amount + (selectedSite.transactionFee ?? 0) : 0;
};

export const roundToClosestMultiple = (base: number, num: number): number => {
  if (base <= 0) {
    throw new Error('Base number must be greater than 0');
  }

  const quotient = num / base;
  const roundedQuotient = Math.ceil(quotient);
  return roundedQuotient * base;
};
